(function () {
  'use strict';

  var drawer = document.querySelector('.oddit-cart-drawer');
  if (!drawer) return;

  // Make sure the drawer is visible for JS-driven state (Liquid sets display:none as an anti-flash guard)
  drawer.style.display = '';

  /* ---------- State ---------- */
  var abortController = new AbortController();
  var signal = abortController.signal;
  var lastFocusedElement = null;

  /* ---------- Utilities ---------- */
  function isFocusable(el) {
    if (!el || el.disabled) return false;
    if (el.tabIndex < 0) return false;
    var rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return false;
    return true;
  }

  function getFocusableElements() {
    var selector = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';
    var nodes = drawer.querySelectorAll(selector);
    var focusable = [];
    for (var i = 0; i < nodes.length; i++) {
      if (isFocusable(nodes[i])) focusable.push(nodes[i]);
    }
    return focusable;
  }

  function trapFocus(e) {
    if (!drawer.classList.contains('is-open')) return;
    if (e.key !== 'Tab') return;
    var focusable = getFocusableElements();
    if (focusable.length === 0) {
      e.preventDefault();
      return;
    }
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ---------- Open / Close ---------- */
  function openDrawer() {
    if (drawer.classList.contains('is-open')) return;
    lastFocusedElement = document.activeElement;
    drawer.classList.add('is-open');
    document.body.classList.add('oddit-cart-drawer-open');
    document.body.style.overflow = 'hidden';
    window.requestAnimationFrame(function () {
      var focusable = getFocusableElements();
      if (focusable.length) {
        var closeBtn = drawer.querySelector('.oddit-cart-drawer__close');
        (closeBtn || focusable[0]).focus();
      }
    });
    document.dispatchEvent(new CustomEvent('cart:drawer:open'));
  }

  function closeDrawer() {
    if (!drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    document.body.classList.remove('oddit-cart-drawer-open');
    document.body.style.overflow = '';
    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      try { lastFocusedElement.focus(); } catch (e) {}
    }
    document.dispatchEvent(new CustomEvent('cart:drawer:close'));
  }

  function toggleDrawer() {
    if (drawer.classList.contains('is-open')) {
      closeDrawer();
    } else {
      openDrawer();
    }
  }

  /* ---------- Section Rendering ---------- */
  // The drawer is a section (`sections/oddit-cart-drawer.liquid`) rendered via the
  // header section group. Its outer element carries id="{{ section.id }}", which is
  // also the id Shopify keys the Section Rendering API response by. Every cart
  // mutation asks Shopify to re-render this section server-side and we swap the fresh
  // markup in — the Liquid is the single source of truth, no client-side templating.
  function getSectionId() {
    if (drawer.id) return drawer.id;
    var wrap = drawer.closest('[id^="shopify-section-"]');
    return wrap ? wrap.id.replace('shopify-section-', '') : '';
  }

  function renderDrawerFromSections(sections) {
    if (!sections) return;
    var id = getSectionId();
    var html = sections[id];
    if (html == null) {
      // Fallback: single-section responses may be keyed unexpectedly.
      var keys = Object.keys(sections);
      if (keys.length === 1) html = sections[keys[0]];
    }
    if (html == null) return;

    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = (id && parsed.getElementById(id)) || parsed.querySelector('.oddit-cart-drawer');
    if (!fresh) return;

    // Swap only the inner content so the outer `drawer` node — and the delegated
    // click/overlay listeners bound to it, plus its is-open state — survive.
    drawer.innerHTML = fresh.innerHTML;
    drawer.style.display = '';
    afterRender();
  }

  function afterRender() {
    bindAddonsCarousel();
    var countEl = drawer.querySelector('[data-cart-count]');
    var count = countEl ? (parseInt(countEl.textContent, 10) || 0) : 0;
    // Header cart badge (and any other listeners) update from this. See oddit-section-header.js.
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: { item_count: count } } }));
  }

  /* ---------- Cart API ---------- */
  function postJson(url, body, errMsg) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) {
        return res.json()
          .catch(function () { return { message: errMsg }; })
          .then(function (err) { throw err; });
      }
      return res.json();
    });
  }

  function getCart() {
    return fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error('Failed to fetch cart');
      return res.json();
    });
  }

  function addToCart(variantId, quantity, properties) {
    quantity = quantity == null ? 1 : quantity;
    var body = {
      id: Number(variantId),
      quantity: Number(quantity),
      sections: getSectionId(),
      sections_url: window.location.pathname
    };
    if (properties && Object.keys(properties).length) {
      body.properties = properties;
    }
    return postJson('/cart/add.js', body, 'Add to cart failed').then(function (data) {
      renderDrawerFromSections(data.sections);
      return data;
    });
  }

  function addMultipleToCart(items) {
    return postJson('/cart/add.js', {
      items: items,
      sections: getSectionId(),
      sections_url: window.location.pathname
    }, 'Batch add to cart failed').then(function (data) {
      renderDrawerFromSections(data.sections);
      return data;
    });
  }

  function updateLineItemQuantity(lineItemKey, quantity) {
    return postJson('/cart/change.js', {
      id: lineItemKey,
      quantity: Number(quantity),
      sections: getSectionId(),
      sections_url: window.location.pathname
    }, 'Update line item failed').then(function (cart) {
      renderDrawerFromSections(cart.sections);
      return cart;
    });
  }

  function removeFromCart(lineItemKey) {
    return updateLineItemQuantity(lineItemKey, 0);
  }

  function updateLineItemToSubscription(lineItemKey, sellingPlanId) {
    return postJson('/cart/change.js', {
      id: lineItemKey,
      selling_plan: Number(sellingPlanId),
      sections: getSectionId(),
      sections_url: window.location.pathname
    }, 'Update selling plan failed').then(function (cart) {
      renderDrawerFromSections(cart.sections);
      return cart;
    });
  }

  function refreshCart() {
    var id = getSectionId();
    return fetch(window.location.pathname + '?section_id=' + encodeURIComponent(id), {
      method: 'GET',
      credentials: 'same-origin'
    }).then(function (res) {
      if (!res.ok) throw new Error('Failed to refresh cart');
      return res.text();
    }).then(function (html) {
      var sections = {};
      sections[id] = html;
      renderDrawerFromSections(sections);
    });
  }

  /* ---------- Add-ons Carousel Progress & Drag ---------- */
  // Track-scoped listeners are (re)bound here after every render, since the track
  // element is replaced by the section swap (its old listeners are GC'd with it).
  // The window resize listener is bound once in init() and re-queries the track.
  function bindAddonsCarousel() {
    var track = drawer.querySelector('[data-addons-track]');
    var progressFill = drawer.querySelector('[data-addons-progress-fill]');
    if (!track) return;

    var scrollbar = drawer.querySelector('.oddit-cart-drawer__addons-progress');

    // Scrollbar thumb: width reflects the visible portion of the track, left
    // reflects the scroll position (a real scrollbar, not a left-anchored fill).
    function updateScrollbar() {
      if (!progressFill) return;
      var maxScroll = track.scrollWidth - track.clientWidth;
      if (maxScroll <= 0) {
        progressFill.style.width = '100%';
        progressFill.style.left = '0';
        return;
      }
      var barWidth = Math.max((track.clientWidth / track.scrollWidth) * 100, 15);
      progressFill.style.width = barWidth + '%';
      progressFill.style.left = (track.scrollLeft / maxScroll) * (100 - barWidth) + '%';
    }

    track.addEventListener('scroll', updateScrollbar, { passive: true });
    updateScrollbar();

    // Drag the thumb to scroll the track; click the bar to jump.
    if (progressFill && scrollbar) {
      var sbDragging = false;
      var sbStartX = 0;
      var sbStartScroll = 0;

      progressFill.addEventListener('pointerdown', function (e) {
        sbDragging = true;
        sbStartX = e.clientX;
        sbStartScroll = track.scrollLeft;
        track.style.scrollBehavior = 'auto';
        try { progressFill.setPointerCapture(e.pointerId); } catch (err) {}
        e.preventDefault();
        e.stopPropagation();
      });

      progressFill.addEventListener('pointermove', function (e) {
        if (!sbDragging) return;
        var maxScroll = track.scrollWidth - track.clientWidth;
        var travel = scrollbar.clientWidth - progressFill.clientWidth;
        if (maxScroll <= 0 || travel <= 0) return;
        var dx = e.clientX - sbStartX;
        track.scrollLeft = sbStartScroll + dx * (maxScroll / travel);
      });

      var sbEnd = function (e) {
        if (!sbDragging) return;
        sbDragging = false;
        track.style.scrollBehavior = '';
        try { progressFill.releasePointerCapture(e.pointerId); } catch (err) {}
      };
      progressFill.addEventListener('pointerup', sbEnd);
      progressFill.addEventListener('pointercancel', sbEnd);

      scrollbar.addEventListener('pointerdown', function (e) {
        if (e.target === progressFill) return;
        var maxScroll = track.scrollWidth - track.clientWidth;
        if (maxScroll <= 0) return;
        var rect = scrollbar.getBoundingClientRect();
        var clickRatio = (e.clientX - rect.left) / rect.width;
        track.scrollLeft = Math.max(0, Math.min(1, clickRatio)) * maxScroll;
      });
    }

    var isDragging = false;
    var dragStartX = 0;
    var dragStartScroll = 0;
    var dragMoved = false;

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      isDragging = true;
      dragMoved = false;
      dragStartX = e.clientX;
      dragStartScroll = track.scrollLeft;
      track.classList.add('is-dragging');
      try { track.setPointerCapture(e.pointerId); } catch (err) {}
    });

    track.addEventListener('pointermove', function (e) {
      if (!isDragging) return;
      var dx = e.clientX - dragStartX;
      if (Math.abs(dx) > 3) dragMoved = true;
      track.scrollLeft = dragStartScroll - dx;
    });

    function endDrag(e) {
      if (!isDragging) return;
      isDragging = false;
      track.classList.remove('is-dragging');
      try { track.releasePointerCapture(e.pointerId); } catch (err) {}
    }
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    track.addEventListener('click', function (e) {
      if (dragMoved) {
        e.preventDefault();
        e.stopPropagation();
        dragMoved = false;
      }
    }, true);

    track.addEventListener('dragstart', function (e) { e.preventDefault(); });
  }

  /* ---------- Event Wiring ---------- */
  function setButtonLoading(btn, loading) {
    if (!btn) return;
    if (loading) {
      var restWidth = btn.getBoundingClientRect().width;
      if (restWidth) btn.style.minWidth = restWidth + 'px';
      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.disabled = false;
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
      btn.style.minWidth = '';
    }
  }

  // Current on-screen quantity for a line item, read from the server-rendered DOM.
  function getLineItemQuantity(el) {
    var item = el.closest && el.closest('[data-line-item]');
    var valueEl = item && item.querySelector('[data-quantity-value]');
    if (!valueEl) return null;
    var q = parseInt(valueEl.textContent, 10);
    return isNaN(q) ? null : q;
  }

  function handleDrawerClick(e) {
    var target = e.target;

    // Close targets
    var closeTarget = target.closest && target.closest('[data-cart-drawer-close]');
    if (closeTarget) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    // Add to cart (cross-sell / addon / rec / bundle upsell)
    var addBtn = target.closest && target.closest('[data-add-to-cart], .oddit-cart-drawer__rec-add[data-variant-id], .oddit-cart-drawer__upsell-cta[data-variant-id]');
    if (addBtn) {
      e.preventDefault();
      var variantId = addBtn.getAttribute('data-variant-id');
      if (!variantId) return;
      setButtonLoading(addBtn, true);
      addToCart(Number(variantId), 1)
        .then(function () {
          openDrawer();
        })
        .catch(function (err) {
          console.error('[oddit-cart-drawer] Add to cart failed:', err);
        })
        .then(function () { setButtonLoading(addBtn, false); });
      return;
    }

    // Bundle multi-add upsell
    var bundleBtn = target.closest && target.closest('[data-add-bundle]');
    if (bundleBtn) {
      e.preventDefault();
      var raw = bundleBtn.getAttribute('data-bundle-items');
      if (!raw) return;
      var items;
      try { items = JSON.parse(raw); } catch (err) { return; }
      setButtonLoading(bundleBtn, true);
      addMultipleToCart(items)
        .then(function () { openDrawer(); })
        .catch(function (err) { console.error('[oddit-cart-drawer] Bundle add failed:', err); })
        .then(function () { setButtonLoading(bundleBtn, false); });
      return;
    }

    // Subscription switch
    var subBtn = target.closest && target.closest('.oddit-cart-drawer__switch-cta[data-selling-plan-id]');
    if (subBtn) {
      e.preventDefault();
      var planId = subBtn.getAttribute('data-selling-plan-id');
      var subKey = subBtn.getAttribute('data-line-item-key') || subBtn.getAttribute('data-line-key');
      if (!planId) return;

      var affectedKeys = [];
      if (subKey) {
        affectedKeys.push(subKey);
      } else {
        // No explicit key — apply to every line currently in the drawer.
        var lineNodes = drawer.querySelectorAll('[data-line-item][data-line-item-key]');
        for (var lk = 0; lk < lineNodes.length; lk++) {
          affectedKeys.push(lineNodes[lk].getAttribute('data-line-item-key'));
        }
      }
      if (!affectedKeys.length) return;

      setButtonLoading(subBtn, true);
      var chain = Promise.resolve();
      affectedKeys.forEach(function (k) {
        chain = chain.then(function () {
          return updateLineItemToSubscription(k, Number(planId));
        });
      });
      chain
        .catch(function (err) { console.error('[oddit-cart-drawer] Subscription switch failed:', err); })
        .then(function () { setButtonLoading(subBtn, false); });
      return;
    }

    // Quantity decrease
    var qtyDecBtn = target.closest && target.closest('[data-quantity-decrease], [data-quantity-action="decrease"]');
    if (qtyDecBtn) {
      e.preventDefault();
      var decKey = qtyDecBtn.getAttribute('data-line-item-key') || qtyDecBtn.getAttribute('data-line-key');
      if (!decKey) return;
      var decQty = getLineItemQuantity(qtyDecBtn);
      if (decQty == null) return;
      setButtonLoading(qtyDecBtn, true);
      updateLineItemQuantity(decKey, Math.max(decQty - 1, 0))
        .catch(function (err) { console.error('[oddit-cart-drawer] Quantity decrease failed:', err); })
        .then(function () { setButtonLoading(qtyDecBtn, false); });
      return;
    }

    // Quantity increase
    var qtyIncBtn = target.closest && target.closest('[data-quantity-increase], [data-quantity-action="increase"]');
    if (qtyIncBtn) {
      e.preventDefault();
      var incKey = qtyIncBtn.getAttribute('data-line-item-key') || qtyIncBtn.getAttribute('data-line-key');
      if (!incKey) return;
      var incQty = getLineItemQuantity(qtyIncBtn);
      if (incQty == null) return;
      setButtonLoading(qtyIncBtn, true);
      updateLineItemQuantity(incKey, incQty + 1)
        .catch(function (err) { console.error('[oddit-cart-drawer] Quantity increase failed:', err); })
        .then(function () { setButtonLoading(qtyIncBtn, false); });
      return;
    }

    // Remove
    var removeBtn = target.closest && target.closest('[data-remove-item], [data-remove-line]');
    if (removeBtn) {
      e.preventDefault();
      var remKey = removeBtn.getAttribute('data-line-item-key') || removeBtn.getAttribute('data-line-key');
      if (!remKey) return;
      setButtonLoading(removeBtn, true);
      removeFromCart(remKey)
        .catch(function (err) { console.error('[oddit-cart-drawer] Remove failed:', err); })
        .then(function () { setButtonLoading(removeBtn, false); });
      return;
    }

    // Checkout CTA
    var checkoutBtn = target.closest && target.closest('.oddit-cart-drawer__checkout-cta, [data-cart-checkout]');
    if (checkoutBtn) {
      e.preventDefault();
      // Show the spinner until the checkout page navigation unloads this page.
      setButtonLoading(checkoutBtn, true);
      window.location.href = '/checkout';
      return;
    }
  }

  /* ---------- Overlay Click (explicit for clarity) ---------- */
  function handleOverlayClick(e) {
    var overlay = drawer.querySelector('.oddit-cart-drawer__overlay');
    if (overlay && e.target === overlay) {
      closeDrawer();
    }
  }

  /* ---------- Init ---------- */
  function init() {
    // Delegated click handler covers open/close, add-to-cart, quantity, remove, checkout, upsells
    drawer.addEventListener('click', handleDrawerClick, { signal: signal });
    drawer.addEventListener('click', handleOverlayClick, { signal: signal });

    // Escape key + focus trap
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeDrawer();
      } else if (e.key === 'Tab' && drawer.classList.contains('is-open')) {
        trapFocus(e);
      }
    }, { signal: signal });

    // Add-ons carousel: window resize re-queries the current (possibly re-rendered) track.
    window.addEventListener('resize', function () {
      var track = drawer.querySelector('[data-addons-track]');
      var fill = drawer.querySelector('[data-addons-progress-fill]');
      if (!track || !fill) return;
      var maxScroll = track.scrollWidth - track.clientWidth;
      if (maxScroll <= 0) {
        fill.style.width = '100%';
        fill.style.left = '0';
        return;
      }
      var barWidth = Math.max((track.clientWidth / track.scrollWidth) * 100, 15);
      fill.style.width = barWidth + '%';
      fill.style.left = (track.scrollLeft / maxScroll) * (100 - barWidth) + '%';
    }, { signal: signal });

    // The drawer is already server-rendered with the current cart on page load —
    // no initial fetch needed; just wire up the track-scoped listeners.
    bindAddonsCarousel();
  }

  function destroy() {
    abortController.abort();
    document.body.classList.remove('oddit-cart-drawer-open');
    document.body.style.overflow = '';
  }

  // Theme editor compatibility
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('.oddit-cart-drawer')) {
      // Section reload re-executes this script since <script> is part of the section markup
    }
  });
  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('.oddit-cart-drawer')) {
      destroy();
    }
  });

  init();

  /* ---------- Public API ---------- */
  window.OdditCartDrawer = {
    open: openDrawer,
    close: closeDrawer,
    toggle: toggleDrawer,
    addToCart: addToCart,
    addMultipleToCart: addMultipleToCart,
    removeFromCart: removeFromCart,
    updateLineItemQuantity: updateLineItemQuantity,
    updateLineItemToSubscription: updateLineItemToSubscription,
    getCart: getCart,
    refreshCart: refreshCart
  };
})();
